/**
 * WorkspaceSelector — workbench anchor control for a chat session.
 *
 * Shows the current workbench mode (free / single / multi) as a clickable
 * breadcrumb at the top of the chat. Clicking opens a popover with the
 * list of editable worktrees from /api/projects and three modes:
 *
 *   - 🧠 Free chat       → []
 *   - 📁 Single repo     → [ path ]
 *   - 🔗 Multi-repo      → [ pathA, pathB, ... ]
 *
 * When the user picks a different set, onChange is called with the new
 * paths. The parent decides what to do (typically: PATCH the session
 * workspaces endpoint, or just hold in local state for a not-yet-saved
 * session and send them with the next POST /api/chat/send).
 */

import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FolderGit2,
  Brain,
  Link2,
  ChevronDown,
  Check,
  GitBranch,
  AlertCircle,
} from "lucide-react";

export type WorkbenchMode = "free" | "single" | "multi";

export interface WorkbenchRepoSummary {
  name: string;
  path: string;
  branch: string;
  isDirty: boolean;
  hasClaudeMd: boolean;
  error: string | null;
}

interface Project {
  name: string;
  path: string;
  kind: "worktree" | "bare";
  branch: string;
  status: string;
  hasWorktree: boolean;
  worktreePath: string | null;
}

interface ProjectsResponse {
  projects: Project[];
}

interface WorkspaceSelectorProps {
  workspacePaths: string[];
  workbenchRepos: WorkbenchRepoSummary[];
  onChange: (paths: string[]) => void;
  disabled?: boolean;
}

export function WorkspaceSelector({
  workspacePaths,
  workbenchRepos,
  onChange,
  disabled,
}: WorkspaceSelectorProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const res = await fetch("/api/projects");
      return res.json() as Promise<ProjectsResponse>;
    },
  });

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const worktrees =
    data?.projects.filter((p) => p.kind === "worktree") ?? [];

  const mode: WorkbenchMode =
    workspacePaths.length === 0
      ? "free"
      : workspacePaths.length === 1
        ? "single"
        : "multi";

  const togglePath = (path: string) => {
    if (workspacePaths.includes(path)) {
      onChange(workspacePaths.filter((p) => p !== path));
    } else {
      onChange([...workspacePaths, path]);
    }
  };

  const setFreeMode = () => {
    onChange([]);
    setOpen(false);
  };

  const setSingleMode = (path: string) => {
    onChange([path]);
    setOpen(false);
  };

  // Label for the breadcrumb
  const label = (() => {
    if (mode === "free") return "Free chat";
    if (mode === "single") {
      const repo = workbenchRepos[0];
      if (repo) {
        return `${repo.name} · ${repo.branch}${repo.isDirty ? " ·dirty" : ""}`;
      }
      // fallback if we haven't hydrated workbenchRepos yet
      const first = workspacePaths[0] ?? "";
      return first.split("/").pop() || first || "repo";
    }
    // multi
    if (workbenchRepos.length > 0) {
      return `${workbenchRepos.length} repos · ${workbenchRepos
        .map((r) => r.name)
        .join(" + ")}`;
    }
    return `${workspacePaths.length} repos`;
  })();

  const icon =
    mode === "free" ? (
      <Brain size={14} className="text-[var(--bw-muted)]" />
    ) : mode === "single" ? (
      <FolderGit2 size={14} className="text-[var(--bw-accent)]" />
    ) : (
      <Link2 size={14} className="text-[var(--bw-accent)]" />
    );

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-[var(--bw-border)] bg-[var(--bw-bg-alt)] hover:border-[var(--bw-accent)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Choose the workbench anchor for this chat"
      >
        {icon}
        <span className="text-[var(--bw-fg)] truncate max-w-[18rem]">
          {label}
        </span>
        <ChevronDown
          size={12}
          className={`text-[var(--bw-muted)] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-20 w-[28rem] max-w-[calc(100vw-1rem)] border border-[var(--bw-border)] bg-[var(--bw-bg)] shadow-2xl">
          {/* Header */}
          <div className="px-3 py-2 border-b border-[var(--bw-border)] flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--bw-muted)]">
              Workbench anchor
            </span>
            {mode !== "free" && (
              <button
                type="button"
                onClick={setFreeMode}
                className="text-[10px] uppercase text-[var(--bw-muted)] hover:text-[var(--bw-accent)]"
              >
                clear
              </button>
            )}
          </div>

          {/* Free mode row */}
          <button
            type="button"
            onClick={setFreeMode}
            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[var(--bw-bg-alt)] border-b border-[var(--bw-border)] ${
              mode === "free" ? "bg-[var(--bw-bg-alt)]" : ""
            }`}
          >
            <Brain size={14} className="text-[var(--bw-muted)] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[var(--bw-fg)]">Free chat</div>
              <div className="text-[10px] text-[var(--bw-muted)]">
                No repo anchor. Research, planning, new project ideas.
              </div>
            </div>
            {mode === "free" && (
              <Check size={14} className="text-[var(--bw-accent)] flex-shrink-0" />
            )}
          </button>

          {/* Worktrees list */}
          <div className="max-h-[60vh] overflow-y-auto">
            {worktrees.length === 0 && (
              <div className="px-3 py-4 text-xs text-[var(--bw-muted)] text-center">
                No editable worktrees.
                <br />
                Clone a bare repo from /projects.
              </div>
            )}

            {worktrees.map((p) => {
              const selected = workspacePaths.includes(p.path);
              return (
                <div
                  key={p.path}
                  className={`flex items-center gap-2 px-3 py-2 hover:bg-[var(--bw-bg-alt)] border-b border-[var(--bw-border)] last:border-b-0 ${
                    selected ? "bg-[var(--bw-bg-alt)]" : ""
                  }`}
                >
                  {/* Multi-select checkbox */}
                  <button
                    type="button"
                    onClick={() => togglePath(p.path)}
                    className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${
                      selected
                        ? "border-[var(--bw-accent)] bg-[var(--bw-accent)]"
                        : "border-[var(--bw-border)] hover:border-[var(--bw-accent)]"
                    }`}
                    title="Toggle in multi-repo selection"
                  >
                    {selected && (
                      <Check size={10} className="text-[var(--bw-bg)]" />
                    )}
                  </button>

                  {/* Single-pick (main click target) */}
                  <button
                    type="button"
                    onClick={() => setSingleMode(p.path)}
                    className="flex-1 min-w-0 text-left flex items-center gap-2"
                    title="Anchor session to this repo only"
                  >
                    <FolderGit2
                      size={13}
                      className="text-[var(--bw-accent)] flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[var(--bw-fg)] truncate">
                        {p.name}
                      </div>
                      <div className="text-[10px] text-[var(--bw-muted)] flex items-center gap-1 truncate">
                        <GitBranch size={9} className="flex-shrink-0" />
                        <span className="truncate">{p.branch}</span>
                        <span className="flex-shrink-0">·</span>
                        <span
                          className={`flex-shrink-0 ${
                            p.status === "dirty"
                              ? "text-[var(--bw-accent)]"
                              : "text-[var(--bw-muted)]"
                          }`}
                        >
                          {p.status}
                        </span>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Mode hint footer */}
          <div className="px-3 py-2 border-t border-[var(--bw-border)] text-[10px] text-[var(--bw-muted)] leading-relaxed">
            <div className="flex items-start gap-1.5">
              <AlertCircle
                size={10}
                className="mt-0.5 flex-shrink-0 text-[var(--bw-muted)]"
              />
              <span>
                <strong>Click</strong> a repo to anchor single-repo.{" "}
                <strong>Checkbox</strong> to toggle multi-repo.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
